import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { Button, Input, Card, Alert, H1, Text } from '../../components/ui';

const loginSchema = z.object({
  organization_code: z.string().min(1, 'School code is required'),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const GuardianLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError } = useGuardianAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/guardian/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.username, data.password, data.organization_code);
      navigate('/guardian/dashboard');
    } catch {
      // Error is handled by the store
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl ring-1 ring-white/30">
              <UserGroupIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="mt-6 text-center font-display text-3xl font-bold text-white">
            Parent/Guardian Portal
          </h1>
          <p className="mt-2 text-center text-primary-200">
            Sign in to view your child's progress
          </p>
        </div>

        <Card className="mt-8 p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <Alert variant="error">
                {error}
              </Alert>
            )}

            <div>
              <label htmlFor="organization_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                School Code
              </label>
              <Input
                {...register('organization_code')}
                id="organization_code"
                type="text"
                placeholder="e.g. PROTEA"
                autoComplete="organization"
                className="mt-1"
                error={errors.organization_code?.message}
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username or Email
              </label>
              <Input
                {...register('username')}
                id="username"
                type="text"
                autoComplete="username"
                className="mt-1"
                error={errors.username?.message}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Password
              </label>
              <div className="mt-1 relative">
                <Input
                  {...register('password')}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  error={errors.password?.message}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/guardian/reset-password"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoading}
              disabled={isLoading}
            >
              Sign in
            </Button>

            <div className="text-center text-sm">
              <p className="text-gray-600 dark:text-gray-400">
                Staff member?{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Sign in here
                </Link>
              </p>
            </div>

            <div className="text-center text-sm">
              <p className="text-gray-600 dark:text-gray-400">
                Have an invitation?{' '}
                <Link to="/guardian/invitation" className="font-medium text-primary-600 hover:text-primary-500">
                  Accept invitation
                </Link>
              </p>
            </div>
          </form>
        </Card>

        <div className="text-center">
          <p className="text-xs text-primary-200">
            By signing in, you agree to our{' '}
            <a href="/legal/terms" className="underline hover:text-white">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/docs/security/popia" className="underline hover:text-white">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};